import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mountain, Check, ArrowRight, ArrowLeft, Plane, ShieldCheck, CreditCard, Lock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PillButton, Overline } from "../components/primitives";
import { fmtDate } from "../lib/date";
import { initRecaptcha, getRecaptchaToken } from "../lib/recaptcha";
import { loadPayPalSdk } from "../lib/paypal";

const FIT = ["Beginner", "Intermediate", "Advanced", "Athlete"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function Field({ label, children, required }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}{required && <span className="text-primary"> *</span>}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
const inputCls = "w-full h-12 rounded-xl border border-black/10 px-4 focus:outline-none focus:ring-2 focus:ring-primary";

export default function Booking() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [retreat, setRetreat] = useState(null);
  const [depId, setDepId] = useState(() => new URLSearchParams(window.location.search).get("departure") || "");
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_legal_name: "", date_of_birth: "", age_confirmed: false, address: "", phone: "", email: "",
    passport_number: "", passport_expiry: "", emergency_contact_name: "", emergency_contact_phone: "",
    dietary_restrictions: "", allergies: "", medical_info: "", injuries_limitations: "",
    fitness_level: "Intermediate", tshirt_size: "M", payment_plan: "deposit", promo_code: "",
    accept_waiver: false, accept_terms: false, accept_cancellation: false, accept_privacy: false, accept_travel_insurance: false,
  });
  const [card, setCard] = useState({ name: "", number: "4111 1111 1111 1111", exp: "12/28", cvc: "123", zip: "94107" });
  const [payMode, setPayMode] = useState("simulation");
  const [qbEnv, setQbEnv] = useState("sandbox");
  const [paypalCfg, setPaypalCfg] = useState(null);
  const ppRef = useRef(null);
  const ppRendered = useRef(false);

  useEffect(() => {
    api.get("/retreats/el-salvador-6day").then((r) => { setRetreat(r.data); setDepId((prev) => prev || r.data.departures?.[0]?.id || ""); }).catch(() => {});
    api.get("/quickbooks/public-config").then((r) => { setPayMode(r.data.mode); setQbEnv(r.data.env); }).catch(() => {});
    api.get("/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => {});
    initRecaptcha();
  }, []);
  useEffect(() => { if (user) setForm((f) => ({ ...f, email: f.email || user.email, full_legal_name: f.full_legal_name || user.name })); }, [user]);

  const ageOK = () => {
    if (!form.date_of_birth) return false;
    const d = new Date(form.date_of_birth); const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
    return a >= 18;
  };
  const canNext0 = () => form.age_confirmed && ageOK() && form.accept_waiver && form.accept_terms && form.accept_cancellation && form.accept_privacy && form.accept_travel_insurance && form.full_legal_name && form.date_of_birth && form.email;
  const ensureBooking = async () => {
    if (booking) return booking;
    if (!canNext0()) { toast.error("Please complete all required fields first."); throw new Error("incomplete"); }
    const res = await api.post("/bookings", { retreat_id: retreat.id, departure_id: depId, ...form });
    setBooking(res.data);
    return res.data;
  };
  useEffect(() => {
    if (step !== 3 || !paypalCfg?.configured || ppRendered.current || !ppRef.current) return;
    let cancelled = false;
    loadPayPalSdk(paypalCfg.client_id).then((ok) => {
      if (!ok || cancelled || !window.paypal || !ppRef.current) return;
      ppRendered.current = true;
      window.paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal" },
        createOrder: async () => {
          const b = await ensureBooking();
          const { data } = await api.post("/paypal/create-order", { booking_id: b.id });
          return data.order_id;
        },
        onApprove: async (data) => {
          try {
            const recaptcha_token = await getRecaptchaToken("payment");
            await api.post("/paypal/capture-order", { order_id: data.orderID, recaptcha_token });
            toast.success("Payment confirmed! Thank you.");
            navigate("/portal", { replace: true });
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Payment could not be verified.");
          }
        },
        onCancel: () => toast("Payment cancelled. You have not been charged."),
        onError: () => toast.error("PayPal encountered an error. Please try again."),
      }).render(ppRef.current);
    });
    return () => { cancelled = true; };
  }, [step, paypalCfg]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (ready && !user) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-secondary/40">
        <div className="max-w-md w-full rounded-3xl bg-white border border-black/10 p-8 text-center">
          <Mountain className="w-8 h-8 text-primary mx-auto" />
          <h1 className="mt-4 font-display font-extrabold text-2xl">Log in to book</h1>
          <p className="mt-2 text-foreground/60 text-sm">Create an account or log in to secure your spot and manage your trip.</p>
          <div className="mt-6 flex flex-col gap-3">
            <PillButton onClick={() => navigate("/register", { state: { from: "/book" } })} data-testid="book-gate-register" className="w-full">Create account</PillButton>
            <PillButton variant="outline" onClick={() => navigate("/login", { state: { from: "/book" } })} data-testid="book-gate-login" className="w-full">Log in</PillButton>
          </div>
        </div>
      </div>
    );
  }
  if (!retreat) return <div className="min-h-screen grid place-items-center text-primary font-display">Loading…</div>;

  const steps = ["Traveler", "Health & Fitness", "Agreements", "Payment"];
  const selDep = retreat.departures?.find((d) => d.id === depId) || null;
  const sched = (() => {
    const price = retreat.price, dep = retreat.deposit_amount;
    if (form.payment_plan === "full") return { first: price, label: "Full payment" };
    if (form.payment_plan === "installments") { const each = Math.round((price - dep) / retreat.installment_count * 100) / 100; return { first: dep, label: `Deposit now, then ${retreat.installment_count} × $${each.toLocaleString()}` }; }
    return { first: dep, label: "Deposit now, balance later" };
  })();

  const canNext = () => {
    if (step === 0) return form.full_legal_name && form.date_of_birth && form.address && form.phone && form.email && form.emergency_contact_name && form.emergency_contact_phone;
    if (step === 1) return form.fitness_level && form.tshirt_size;
    if (step === 2) return form.age_confirmed && ageOK() && form.accept_waiver && form.accept_terms && form.accept_cancellation && form.accept_privacy && form.accept_travel_insurance;
    return true;
  };

  const tokenizeCard = async () => {
    // Real QuickBooks tokenization — card data goes straight to Intuit, never our server.
    const base = qbEnv === "production" ? "https://api.intuit.com" : "https://sandbox.api.intuit.com";
    const [mm, yy] = (card.exp || "").split("/").map((s) => s.trim());
    const resp = await fetch(`${base}/quickbooks/v4/payments/tokens`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        card: {
          number: (card.number || "").replace(/\s/g, ""),
          expMonth: mm,
          expYear: yy && yy.length === 2 ? `20${yy}` : yy,
          cvc: card.cvc,
          name: card.name,
          address: { postalCode: card.zip, region: "", country: "US" },
        },
      }),
    });
    if (!resp.ok) throw new Error("We couldn't verify your card. Please check the details and try again.");
    const data = await resp.json();
    if (!data.value) throw new Error("Card verification failed. Please try again.");
    return data.value;
  };

  const createBookingAndPay = async () => {
    setBusy(true);
    try {
      let b = booking;
      if (!b) {
        const res = await api.post("/bookings", { retreat_id: retreat.id, departure_id: depId, ...form });
        b = res.data; setBooking(b);
      }
      let token = "tok_" + Math.random().toString(36).slice(2);
      if (payMode === "quickbooks") {
        token = await tokenizeCard();
      }
      const idem = `${b.id}-${Date.now()}`;
      const recaptcha_token = await getRecaptchaToken("payment");
      const pay = await api.post("/payments", { booking_id: b.id, card_token: token, idempotency_key: idem, recaptcha_token });
      toast.success(`Payment confirmed! ${pay.data.mode === "simulation" ? "(test mode)" : ""}`);
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(err?.message && !err.response ? err.message : formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="glass-nav bg-white/80 border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/retreat")} className="flex items-center gap-2 font-display font-extrabold"><Mountain className="w-5 h-5 text-primary" /> La Vida Is Travel Fit</button>
          <div className="text-sm text-foreground/60 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Secure checkout</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {/* Steps indicator */}
          <div className="flex items-center gap-2 mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 text-sm ${i <= step ? "text-primary font-semibold" : "text-foreground/40"}`}>
                  <span className={`w-7 h-7 rounded-full grid place-items-center text-xs ${i < step ? "bg-primary text-white" : i === step ? "border-2 border-primary" : "border border-black/15"}`}>
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-primary" : "bg-black/10"}`} />}
              </React.Fragment>
            ))}
          </div>

          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="rounded-3xl bg-white border border-black/10 p-8">
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="font-display font-extrabold text-2xl">Traveler details</h2>
                {retreat.departures?.length > 0 && (
                  <Field label="Retreat date" required>
                    <select className={inputCls} data-testid="book-departure" value={depId} onChange={(e) => setDepId(e.target.value)}>
                      {retreat.departures.map((d) => <option key={d.id} value={d.id}>{fmtDate(d.start_date)} – {fmtDate(d.end_date)} · {d.label}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Full legal name (as on passport)" required><input className={inputCls} data-testid="book-name" value={form.full_legal_name} onChange={(e) => set("full_legal_name", e.target.value)} /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Date of birth" required><input type="date" className={inputCls} data-testid="book-dob" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
                  <Field label="Phone" required><input className={inputCls} data-testid="book-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                </div>
                <Field label="Email" required><input type="email" className={inputCls} data-testid="book-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
                <Field label="Address" required><input className={inputCls} data-testid="book-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Passport number"><input className={inputCls} data-testid="book-passport" value={form.passport_number} onChange={(e) => set("passport_number", e.target.value)} /></Field>
                  <Field label="Passport expiry"><input type="date" className={inputCls} value={form.passport_expiry} onChange={(e) => set("passport_expiry", e.target.value)} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Emergency contact name" required><input className={inputCls} data-testid="book-ec-name" value={form.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} /></Field>
                  <Field label="Emergency contact phone" required><input className={inputCls} data-testid="book-ec-phone" value={form.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} /></Field>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-display font-extrabold text-2xl">Health & fitness</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Fitness level" required>
                    <select className={inputCls} data-testid="book-fitness" value={form.fitness_level} onChange={(e) => set("fitness_level", e.target.value)}>{FIT.map((f) => <option key={f}>{f}</option>)}</select>
                  </Field>
                  <Field label="T-shirt size" required>
                    <select className={inputCls} data-testid="book-tshirt" value={form.tshirt_size} onChange={(e) => set("tshirt_size", e.target.value)}>{SIZES.map((s) => <option key={s}>{s}</option>)}</select>
                  </Field>
                </div>
                <Field label="Dietary restrictions"><input className={inputCls} value={form.dietary_restrictions} onChange={(e) => set("dietary_restrictions", e.target.value)} /></Field>
                <Field label="Allergies"><input className={inputCls} value={form.allergies} onChange={(e) => set("allergies", e.target.value)} /></Field>
                <Field label="Injuries / limitations"><input className={inputCls} value={form.injuries_limitations} onChange={(e) => set("injuries_limitations", e.target.value)} /></Field>
                <Field label="Medical information"><textarea rows={3} className="w-full rounded-xl border border-black/10 p-4 focus:outline-none focus:ring-2 focus:ring-primary" value={form.medical_info} onChange={(e) => set("medical_info", e.target.value)} /></Field>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <h2 className="font-display font-extrabold text-2xl">Agreements</h2>
                <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="book-age" checked={form.age_confirmed} onChange={(e) => set("age_confirmed", e.target.checked)} className="mt-1 w-5 h-5 accent-[#D95D39]" />
                    <span className="text-sm"><b>I confirm I am 18 years of age or older.</b> All retreats are strictly for adults 18+.</span>
                  </label>
                  {form.date_of_birth && !ageOK() && <div className="mt-2 text-sm text-destructive" data-testid="book-age-error">Based on your date of birth, you do not meet the 18+ requirement.</div>}
                </div>
                {[
                  ["accept_terms", "I accept the Terms & Conditions.", "/legal/terms"],
                  ["accept_waiver", "I accept the Travel Waiver & Release of Liability.", "/legal/waiver"],
                  ["accept_cancellation", "I accept the Cancellation & Refund Policy.", "/legal/cancellation"],
                  ["accept_privacy", "I accept the Privacy Policy.", "/legal/privacy"],
                  ["accept_travel_insurance", "I acknowledge the Travel Insurance recommendation.", "/legal/insurance"],
                ].map(([k, label, link]) => (
                  <label key={k} className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid={`book-${k}`} checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 w-5 h-5 accent-[#D95D39]" />
                    <span className="text-sm">{label} <a href={link} target="_blank" rel="noreferrer" className="text-primary underline">Read</a></span>
                  </label>
                ))}
              </div>
            )}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="font-display font-extrabold text-2xl">Payment</h2>
                <div>
                  <Overline>Choose your plan</Overline>
                  <div className="mt-3 grid gap-3">
                    {[
                      ["deposit", "Deposit", `Pay $${retreat.deposit_amount.toLocaleString()} now, balance later`],
                      ["full", "Pay in full", `Pay $${retreat.price.toLocaleString()} today`],
                      ["installments", "Installments", `$${retreat.deposit_amount.toLocaleString()} deposit + ${retreat.installment_count} monthly payments`],
                    ].map(([v, t, d]) => (
                      <label key={v} data-testid={`plan-${v}`} className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${form.payment_plan === v ? "border-primary bg-primary/5" : "border-black/10"}`}>
                        <input type="radio" name="plan" checked={form.payment_plan === v} onChange={() => set("payment_plan", v)} className="w-5 h-5 accent-[#D95D39]" />
                        <div><div className="font-semibold">{t}</div><div className="text-sm text-foreground/60">{d}</div></div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Promo code (optional)</label>
                  <input className={inputCls + " mt-1.5 uppercase"} data-testid="book-promo" value={form.promo_code} onChange={(e) => set("promo_code", e.target.value)} placeholder="Enter code" />
                </div>
                <div className="rounded-2xl border border-black/10 p-5">
                  <div className="flex items-center gap-2 text-sm font-medium"><CreditCard className="w-4 h-4 text-primary" /> Card details <span className="ml-auto text-xs text-foreground/50">via QuickBooks Payments</span></div>
                  <div className="mt-4 space-y-3">
                    <input className={inputCls} placeholder="Cardholder name" data-testid="card-name" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} />
                    <input className={inputCls} placeholder="Card number" data-testid="card-number" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} />
                    <div className="grid grid-cols-3 gap-3">
                      <input className={inputCls} placeholder="MM/YY" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} />
                      <input className={inputCls} placeholder="CVC" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} />
                      <input className={inputCls} placeholder="ZIP" value={card.zip} onChange={(e) => setCard({ ...card, zip: e.target.value })} />
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-foreground/50 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Card data is processed securely. Test mode until QuickBooks is connected.</div>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              {step > 0 ? <PillButton variant="outline" onClick={() => setStep(step - 1)} data-testid="book-back"><ArrowLeft className="w-4 h-4" /> Back</PillButton> : <span />}
              {step < 3 ? (
                <PillButton onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete required fields.")} data-testid="book-next">Continue <ArrowRight className="w-4 h-4" /></PillButton>
              ) : paypalCfg?.configured ? (
                <div ref={ppRef} data-testid="paypal-buttons" className="w-full max-w-xs" />
              ) : (
                <PillButton onClick={createBookingAndPay} disabled={busy} data-testid="book-pay">{busy ? "Processing…" : `Pay $${sched.first.toLocaleString()}`} <Lock className="w-4 h-4" /></PillButton>
              )}
            </div>
          </motion.div>
        </div>

        {/* Summary */}
        <div>
          <div className="lg:sticky lg:top-24 rounded-3xl bg-white border border-black/10 p-6">
            <img src={retreat.hero_image} alt="" className="rounded-2xl h-36 w-full object-cover" />
            <div className="mt-4 font-display font-bold text-lg leading-tight">{retreat.name}</div>
            <div className="mt-1 text-sm text-primary flex items-center gap-1.5"><Plane className="w-3.5 h-3.5" /> Flights included</div>
            <div className="mt-4 space-y-2 text-sm border-t border-black/5 pt-4">
              <div className="flex justify-between"><span className="text-foreground/60">Departure</span><span data-testid="summary-departure">{selDep ? `${fmtDate(selDep.start_date)} – ${fmtDate(selDep.end_date)}` : fmtDate(retreat.start_date)}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Total price</span><span>${retreat.price.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Deposit</span><span>${retreat.deposit_amount.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Remaining balance</span><span>${(retreat.price - retreat.deposit_amount).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Plan</span><span className="capitalize">{form.payment_plan}</span></div>
            </div>
            <div className="mt-4 border-t border-black/5 pt-4 flex justify-between items-baseline">
              <span className="text-sm text-foreground/60">Due today</span>
              <span className="font-display font-extrabold text-2xl">${sched.first.toLocaleString()}</span>
            </div>
            <p className="mt-1 text-xs text-foreground/50">{sched.label}</p>
            <div className="mt-4 rounded-xl bg-secondary/60 p-3 text-xs text-foreground/60 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Adults 18+ · Secure payment</div>
          </div>
        </div>
      </div>
    </div>
  );
}
